import React, { useState } from 'react';
import PropTypes from "prop-types";
import { Table, Button, UncontrolledTooltip, Badge } from 'reactstrap';
import { translate } from "react-translate";
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';
import { actionSetPatientEditData } from 'store/actions/usersActions';
import { useDispatch } from "react-redux";
import { useHistory } from 'react-router-dom';
import PatientDetailsModal from './PatientDetailsModal';

/**
 * @component TablePatientUsers
 * @description Renders the patient management table. Uses `total_practices` from
 * the list payload (numeric field) instead of the removed `associated_details`
 * array. Detail hydration is deferred to the modal via a server-side call.
 *
 * @param {Object}  props
 * @param {Array}   props.data    - Array of patient objects from the paginated list.
 * @param {boolean} props.loading - When true renders a local loading spinner.
 */
const TablePatientUsers = (props) => {
  const { data, loading } = props;
  const { t } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  /**
   * Navigates to the patient edit form and seeds Redux with the selected row.
   *
   * @param {React.SyntheticEvent} e
   * @param {Object} user - Patient row object.
   */
  const editUser = (e, user) => {
    e.preventDefault();
    dispatch(actionSetPatientEditData({ ...user }));
    history.push(`/admin/list-patients/edit/`);
  };

  /**
   * Opens the details modal for the selected patient.
   * The modal is responsible for fetching `associated_details` server-side.
   *
   * @param {React.SyntheticEvent} e
   * @param {Object} user - Patient row object.
   */
  const viewDetails = (e, user) => {
    e.preventDefault();
    setSelectedPatient(user);
    setModalOpen(true);
  };

  /** @returns {void} */
  const toggleModal = () => setModalOpen((prev) => !prev);

  return (
    <>
      <PatientDetailsModal
        isOpen={modalOpen}
        toggle={toggleModal}
        patient={selectedPatient}
      />

      {loading ? (
        <LoadingLocal />
      ) : (
        <div className="table-data">
          <Table responsive>
            <thead className="text-primary">
              <tr>
                <th>{t("firstName-user")}</th>
                <th>{t("lastName-user")}</th>
                <th>{t("email-user")}</th>
                <th>{t("type-user")}</th>
                <th>{"Verified"}</th>
                <th className="text-center">{t("relations-user")}</th>
                <th className="text-right">{t("actions")}</th>
              </tr>
            </thead>

            {data.length > 0 &&
              data.map((p, index) => (
                <tbody key={index}>
                  <tr>
                    <td>{p.firstName}</td>
                    <td>{p.lastName}</td>
                    <td>{p.email}</td>
                    <td>
                      <Badge color="info">{p.membership}</Badge>
                    </td>
                    <td>
                      <Badge color={p.is_ready ? 'success' : 'warning'}>
                        {p.is_ready ? 'Yes' : 'No'}
                      </Badge>
                    </td>

                    <td className="text-center">
                      <div className="summary-badge">
                        <i className="fa fa-hospital-o mr-1" />
                        <span className="summary-highlight">
                          {typeof p.total_practices === 'number' ? p.total_practices : '-'}
                        </span>{' '}
                        {t("practice-count")}
                      </div>
                    </td>

                    <td className="text-right">
                      <Button
                        onClick={(e) => viewDetails(e, p)}
                        className="btn-icon btn-round btn-neutral"
                        color="info"
                        size="sm"
                        id={`tooltip-details-pat-${index}`}
                      >
                        <i className="fa fa-eye" />
                      </Button>
                      <UncontrolledTooltip placement="top" target={`tooltip-details-pat-${index}`}>
                        {t("view-details")}
                      </UncontrolledTooltip>

                      <Button
                        onClick={(e) => editUser(e, p)}
                        className="btn-icon btn-round btn-neutral"
                        color="warning"
                        size="sm"
                        id={`tooltip-edit-pat-${index}`}
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      <UncontrolledTooltip placement="top" target={`tooltip-edit-pat-${index}`}>
                        {t("edit-action")}
                      </UncontrolledTooltip>
                    </td>
                  </tr>
                </tbody>
              ))}
          </Table>
        </div>
      )}
    </>
  );
};

TablePatientUsers.defaultProps = {
  data: [],
  loading: true,
};

TablePatientUsers.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
};

export default translate("userList-table")(TablePatientUsers);